// Keeps Tab and Shift+Tab inside an open dialog (Sprint 1 adversarial review, L9). Both modals — the shared Modal and the
// Control PIN prompt — mount it; focus that has left the dialog (a touch keypad focuses nothing; a field went disabled
// under the finger) comes back to the first control on the next Tab. Escape and Enter are the dialogs' own business.
//
// Only the topmost modal dialog traps: a Control PIN prompt raised over a modal — the kiosk's exit over the notice, "Exit
// patient mode" over the stray-handover notice — owns Tab, and the modal beneath stands aside until it closes. A non-modal
// `role="dialog"` (the branch switcher's dropdown) never takes the trap from a modal.
import { useEffect, type RefObject } from "react";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The dialog's tabbable controls in document order. */
export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hidden && el.getAttribute("aria-hidden") !== "true");
}

/** The dialog on top: the last open modal `[role="dialog"]` in document order — a Control PIN prompt raised over a modal comes later in the DOM. */
export function topmostDialog(): Element | null {
  const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
  return dialogs.length > 0 ? dialogs[dialogs.length - 1]! : null;
}

export function useFocusTrap(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const root = ref.current;
      if (e.key !== "Tab" || !root) return;
      // Only the topmost dialog traps Tab: a PIN prompt opened from inside a modal (the kiosk's exit over the notice) must be usable (22 Sep 2026).
      const top = topmostDialog();
      if (top && top !== root) return;
      const items = focusableIn(root);
      if (items.length === 0) { e.preventDefault(); return; }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !root.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    // On the window, not the dialog: a Tab pressed while nothing inside has focus must still be caught.
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ref]);
}
